/**
 * ScoringScaleChoice — the scale people score each option on, chosen at setup.
 *
 * Every setup screen for a method that scores options against criteria shows
 * this one control: the Session app's setup and the session wizard's Value
 * Range step. Before it, neither asked — the wizard drew min/max boxes and
 * then never saved them, and the scoring screen forced 1–5 on every session.
 *
 * The choices and the recommended default come from lib/toolSettings (the
 * workspace's standard scales); the caller saves the choice with
 * `scoringScaleToolConfig`, and the scoring screen reads it back through
 * `useToolSettings`.
 */
import { useId } from 'react';
import { ChoiceGroup, Radio } from '@/components/ds';
import {
  DEFAULT_SCORING_SCALE,
  SCORING_SCALE_CHOICES,
  isSameScoringScale,
  scoringScaleLabel,
  type ScoringScaleRange,
} from '@/lib/toolSettings';

export interface ScoringScaleChoiceProps {
  value: ScoringScaleRange;
  onChange: (scale: ScoringScaleRange) => void;
  legend?: string;
}

export function ScoringScaleChoice({ value, onChange, legend = 'Scale' }: ScoringScaleChoiceProps) {
  const name = useId();
  return (
    <div data-testid="scoring-scale-choice">
      <ChoiceGroup
        legend={legend}
        orientation="horizontal"
        hint={`Everyone scores each option from ${value.min} (low) to ${value.max} (high) on every criterion.`}
      >
        {SCORING_SCALE_CHOICES.map((choice) => (
          <Radio
            key={scoringScaleLabel(choice)}
            name={name}
            checked={isSameScoringScale(choice, value)}
            onChange={() => onChange({ min: choice.min, max: choice.max })}
            label={scoringScaleLabel(choice)}
            description={
              isSameScoringScale(choice, DEFAULT_SCORING_SCALE) ? `${choice.hint}, recommended` : choice.hint
            }
            data-testid={`scoring-scale-${choice.min}-${choice.max}`}
          />
        ))}
      </ChoiceGroup>
    </div>
  );
}
